import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Bell, Settings, ChevronDown, UserCircle, Shield, CheckCircle } from 'lucide-react';
import axiosInstance from '../services/axiosInstance';
import styles from '../styles/ProfilePage.module.css';

// Import the base URL logic from axiosInstance or define it here
const getBaseUrl = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  // if (isProduction) {
  //   return 'https://stockkhata.in'; // Direct backend URL
  if (isProduction) {
    return 'https://stockkhata-webapp.onrender.com'; // Direct backend URL
  } else if (window.location.hostname === 'localhost') {
    return 'http://localhost:5000'; // Local development
  } 
};

const BASE_URL = getBaseUrl();

const Navbar = ({ user, onLogout, isFullyVerified }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={styles.navbar}>
      <div className={styles.navbarContainer}>
        <div className={styles.navbarLogo}>
          <span className={styles.logoText}>
            {user.name} {isFullyVerified && <CheckCircle size={16} className={styles.verifiedIcon} />}
          </span>
        </div>
        <div className={styles.navbarLinks}>
          <button className={styles.navButton}><Bell size={20} /></button>
          <button className={styles.navButton}><Settings size={20} /></button>
          <div className={styles.profileSection} ref={profileRef}>
            <button className={styles.profileButton} onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <User size={20} />
              <ChevronDown size={16} />
            </button>
            <div className={`${styles.profileMenu} ${showProfileMenu ? styles.active : ''}`}>
              <a href="/profile" className={styles.menuItem}><UserCircle size={18} /> Profile</a>
              <a href="/blogs" className={styles.menuItem}><Settings size={18} /> Community Blogs</a>
              <a href="/help" className={styles.menuItem}><Shield size={18} /> Help</a>
              <hr className={styles.menuDivider} />
              <button onClick={onLogout} className={styles.logoutButton}><LogOut size={18} /> Logout</button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/auth/profile');
      if (response.data.success) {
        const profileData = {
          ...response.data.data,
          profile_picture: response.data.data.profile_picture ? `${BASE_URL}${response.data.data.profile_picture}` : ''
        };
        setProfile(profileData);
        setFormData(profileData);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleEdit = () => setIsEditing(true);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'profile_picture' && value instanceof File) {
        form.append(key, value);
      } else if (value) {
        form.append(key, value);
      }
    });

    try {
      const response = await axiosInstance.post('/auth/profile/update', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        const updatedProfile = {
          ...profile,
          ...formData,
          profile_picture: formData.profile_picture instanceof File ? URL.createObjectURL(formData.profile_picture) : profile.profile_picture
        };
        setProfile(updatedProfile);
        setIsEditing(false);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to update profile');
    }
  };

  const calculateProfileCompletion = () => {
    const fields = ['name', 'email', 'phone', 'experience', 'investment_style', 'goals', 'bio'];
    const filled = fields.filter(field => profile?.[field]).length;
    return Math.round((filled / fields.length) * 100);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const nameParts = name.trim().split(' ');
    return `${nameParts[0]?.charAt(0).toUpperCase() || ''}${nameParts.length > 1 ? nameParts[nameParts.length - 1]?.charAt(0).toUpperCase() : ''}`;
  };

  const isFullyVerified = calculateProfileCompletion() === 100;

  if (loading) return <div className={styles.loader}>Loading profile...</div>;
  if (error) return (
    <div className={styles.error}>
      <p>{error}</p>
      <button onClick={fetchProfile} className={styles.retryButton}>Retry</button>
    </div>
  );
  if (!profile) return <div className={styles.noData}>No profile data available</div>;

  return (
    <>
      <Navbar user={profile} onLogout={handleLogout} isFullyVerified={isFullyVerified} />
      <div className={styles.container}>
        <div className={styles.profileCard}>
          <div className={styles.profileHeader}>
            <div className={styles.avatarContainer}>
              {profile.profile_picture ? (
                <img src={profile.profile_picture} alt="Profile" className={styles.avatar} />
              ) : (
                <div className={styles.avatarPlaceholder}>{getInitials(profile.name)}</div>
              )}
              {isEditing && (
                <input
                  type="file"
                  name="profile_picture"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleChange}
                  className={styles.fileInput}
                />
              )}
            </div>
            <div className={styles.headerInfo}>
              <h2 className={styles.name}>
                {profile.name} {isFullyVerified && <CheckCircle size={20} className={styles.verifiedIcon} />}
              </h2>
              <div className={styles.progressContainer}>
                <div className={styles.progressBar} style={{ width: `${calculateProfileCompletion()}%` }} />
                <span className={styles.progressText}>{calculateProfileCompletion()}% Complete</span>
              </div>
            </div>
          </div>

          {!isEditing ? (
            <div className={styles.infoSection}>
              <ProfileField label="Email" value={profile.email} />
              <ProfileField label="Phone" value={profile.phone} />
              <ProfileField label="Experience" value={profile.experience || 'Not specified'} />
              <ProfileField label="Investment Style" value={profile.investment_style || 'Not specified'} />
              <ProfileField label="Goals" value={profile.goals || 'Not specified'} />
              <ProfileField label="Bio" value={profile.bio || 'Not specified'} />
              <button className={styles.editButton} onClick={handleEdit}>Edit Profile</button>
            </div>
          ) : (
            <form className={styles.editForm} onSubmit={handleSubmit}>
              <EditField label="Name" name="name" value={formData.name} onChange={handleChange} />
              <EditField label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
              <EditField
                label="Experience"
                name="experience"
                type="select"
                value={formData.experience}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Select experience' },
                  { value: 'Beginner (0-1 year)', label: 'Beginner (0-1 year)' },
                  { value: 'Intermediate (1-3 years)', label: 'Intermediate (1-3 years)' },
                  { value: 'Experienced (3-5 years)', label: 'Experienced (3-5 years)' },
                  { value: 'Veteran (5+ years)', label: 'Veteran (5+ years)' }
                ]}
              />
              <EditField
                label="Investment Style"
                name="investment_style"
                type="select"
                value={formData.investment_style}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Select style' },
                  { value: 'Value Investing', label: 'Value Investing' },
                  { value: 'Growth Investing', label: 'Growth Investing' },
                  { value: 'Dividend Investing', label: 'Dividend Investing' },
                  { value: 'Day Trading', label: 'Day Trading' },
                  { value: 'Long-term Holding', label: 'Long-term Holding' }
                ]}
              />
              <EditField label="Goals" name="goals" value={formData.goals} onChange={handleChange} textarea />
              <EditField label="Bio" name="bio" value={formData.bio} onChange={handleChange} textarea />
              <div className={styles.formActions}>
                <button type="button" className={styles.cancelButton} onClick={() => setIsEditing(false)}>Cancel</button>
                <button type="submit" className={styles.saveButton}>Save</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

const ProfileField = ({ label, value }) => (
  <div className={styles.infoItem}>
    <span className={styles.label}>{label}:</span>
    <span className={styles.value}>{value}</span>
  </div>
);

const EditField = ({ label, name, value, onChange, type = 'text', options, textarea }) => (
  <div className={styles.editField}>
    <label className={styles.editLabel}>{label}</label>
    {textarea ? (
      <textarea name={name} value={value || ''} onChange={onChange} className={styles.editInput} rows={3} />
    ) : type === 'select' ? (
      <select name={name} value={value || ''} onChange={onChange} className={styles.editInput}>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    ) : (
      <input type={type} name={name} value={value || ''} onChange={onChange} className={styles.editInput} />
    )}
  </div>
);

export default ProfilePage;